import { maybe, type Maybe } from '@passionware/monads';
import type { ValidatedRow } from 'v5/platform/row-level-validation';

export type AdjacentCellRole<TEntity> =
  | { type: 'omit' }
  | { type: 'render'; rowSpan: number; entity: TEntity };

type GroupingKeyFn<TEntity> = (entity: TEntity) => Maybe<string | number>;

/**
 * Adjacent valid rows with the same grouping key share one cell (`rowSpan`).
 * Invalid row at `index` → `omit`. Invalid neighbors break the run.
 * Absent keys never group.
 */
export function resolveAdjacentGroupCellRole<
  TEntity extends { id: string | number }
>(
  items: readonly ValidatedRow<TEntity>[],
  index: number,
  getGroupingKey: GroupingKeyFn<TEntity>
): AdjacentCellRole<TEntity> {
  const row = items[index];
  if (!row?.isValid) {
    return { type: 'omit' };
  }

  const key = getGroupingKey(row.data);

  const matches = (i: number) => {
    if (maybe.isAbsent(key)) return false;
    const other = items[i];
    if (!other?.isValid) return false;
    return Object.is(getGroupingKey(other.data), key);
  };

  if (matches(index - 1)) {
    return { type: 'omit' };
  }

  let rowSpan = 1;
  while (matches(index + rowSpan)) rowSpan += 1;
  return { type: 'render', rowSpan, entity: row.data };
}

export type AdjacentGroupChunk<TEntity extends { id: string | number }> = {
  startIndex: number;
  items: ValidatedRow<TEntity>[];
  /** True when this chunk is a multi-row adjacent group (`rowSpan` > 1). */
  isMultiRowGroup: boolean;
};

/**
 * Split rows into runs that share an adjacent grouping key (for one `tbody` per group).
 * Invalid rows and absent keys are singleton chunks.
 */
export function chunkAdjacentGroups<TEntity extends { id: string | number }>(
  items: readonly ValidatedRow<TEntity>[],
  getGroupingKey: GroupingKeyFn<TEntity>
): AdjacentGroupChunk<TEntity>[] {
  const chunks: AdjacentGroupChunk<TEntity>[] = [];
  let index = 0;
  while (index < items.length) {
    const row = items[index];
    if (!row) break;

    if (!row.isValid) {
      chunks.push({
        startIndex: index,
        items: [row],
        isMultiRowGroup: false
      });
      index += 1;
      continue;
    }

    const role = resolveAdjacentGroupCellRole(items, index, getGroupingKey);
    const rowSpan = role.type === 'render' ? role.rowSpan : 1;
    chunks.push({
      startIndex: index,
      items: items.slice(index, index + rowSpan),
      isMultiRowGroup: rowSpan > 1
    });
    index += rowSpan;
  }
  return chunks;
}
